import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Checkbox } from "components/Checkbox";
import { Accordion } from "components/Accordion";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import { createClientGroup, updateClientGroup } from "./api";
import styles from "./styles.module.scss";

const DISCOUNT_FIELDS = [
  { field: "startpercent", label: "Начальный % скидки", symbol: "%" },
  { field: "shiftpercent", label: "Шаг скидки" },
  { field: "shiftpercentsum", label: "Период увеличения скидки" },
  { field: "discountpercent", label: "Текущий % скидки", symbol: "%" },
  { field: "endpercent", label: "Конечный % скидки", symbol: "%" },
  { field: "nextdiscountsum", label: "Следующее увеличение скидки" },
];

const BONUS_FIELDS = [
  { field: "startbonus", label: "Начальный % бонуса", symbol: "%" },
  { field: "shiftbonus", label: "Шаг бонуса" },
  { field: "shiftbonussum", label: "Период увеличения бонуса" },
  { field: "bonuspercent", label: "Текущий % бонуса", symbol: "%" },
  { field: "endbonus", label: "Конечный % бонуса", symbol: "%" },
  { field: "nextbonussum", label: "Следующее увеличение бонуса" },
  { field: "startbonussum", label: "Начальная величина бонуса" },
  { field: "maxbonuspay", label: "Процент оплаты бонусами" },
];

export const CounterFieldsGrid = ({ fields, values, onChange }) => (
  <div className={styles.counterGrid}>
    {fields.map(({ field, label, symbol }) => (
      <div className={styles.counterField} key={field}>
        <RegularText size={13} className={styles.counterLabel}>
          {label}
          {symbol ? ` (${symbol})` : ""}
        </RegularText>
        <Input
          type="number"
          value={values[field] ?? ""}
          onChange={(ev) => onChange(field, ev.target.value)}
          inputWidth={100}
        />
      </div>
    ))}
  </div>
);

/** Создание / редактирование группы клиентов */
export const ClientGroupModal = ({ isEdit, group }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const [name, setName] = useState(isEdit ? (group?.name ?? "") : "");
  const [available, setAvailable] = useState(
    isEdit ? Boolean(group?.avaliable) : true
  );

  const [values, setValues] = useState(() => {
    const initial = {};
    [...DISCOUNT_FIELDS, ...BONUS_FIELDS].forEach(({ field }) => {
      initial[field] = isEdit ? (group?.[field] ?? 0) : 0;
    });
    return initial;
  });

  const [discountsCollapsed, setDiscountsCollapsed] = useState(false);
  const [bonusesCollapsed, setBonusesCollapsed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const onFieldChange = (field, value) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError("");
    try {
      const numeric = {};
      Object.entries(values).forEach(([key, val]) => {
        numeric[key] = Number(val) || 0;
      });
      const payload = {
        name: name.trim(),
        avaliable: available ? 1 : 0,
        ...numeric,
      };
      if (isEdit) {
        await updateClientGroup({
          ...payload,
          clientgroupid: group.clientgroupid,
        });
      } else {
        await createClientGroup(payload);
      }
      queryClient.invalidateQueries(["client-groups"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell width={860}>
      <ModalHeader
        title={isEdit ? group?.name : name || "Новая группа"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.groupTopRow}>
          <Input
            label="Название"
            labelWidth={80}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            inputWidth={283}
          />
          {isEdit && (
            <Input
              label="Код"
              labelWidth={40}
              value={group?.clientgroupid ?? ""}
              disabled
              inputWidth={140}
            />
          )}
          <Checkbox
            label="Активна"
            checked={available}
            onChange={setAvailable}
          />
        </div>

        <VerticalSpace size={16} />

        <Accordion
          withIcon
          filled
          collapsed={discountsCollapsed}
          handleCollapse={() => setDiscountsCollapsed((v) => !v)}
          parent={<MediumText size={16}>Скидки</MediumText>}
        >
          <CounterFieldsGrid
            fields={DISCOUNT_FIELDS}
            values={values}
            onChange={onFieldChange}
          />
        </Accordion>

        <VerticalSpace size={16} />

        <Accordion
          withIcon
          filled
          collapsed={bonusesCollapsed}
          handleCollapse={() => setBonusesCollapsed((v) => !v)}
          parent={<MediumText size={16}>Бонусы</MediumText>}
        >
          <CounterFieldsGrid
            fields={BONUS_FIELDS}
            values={values}
            onChange={onFieldChange}
          />
        </Accordion>

        {!!error && (
          <>
            <VerticalSpace size={8} />
            <RegularText size={13} color="danger">
              {error}
            </RegularText>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          disabled={!name.trim()}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
